import { ITEM_TYPES } from 'constants/itemType';
import { NotificationPriority, NotificationType } from 'hooks/useNotification';
import type { OrgEventChecklistItemDef } from 'hooks/orgEvents/orgEventAutomationHooks';
import type { UserNotificationWithDetails } from 'hooks/useUserNotifications';
import type {
  MemphisAssetAtRisk,
  MemphisRegionalThreats,
} from './memphisThreatNotification';

/** Stable id for the hardcoded Government of Ontario winter-storm inbox item. */
export const ONTARIO_GTA_THREAT_NOTIFICATION_ID =
  'demo-ontario-gta-winter-storm';

export const ONTARIO_GTA_ASSETS_LAYER_ID = 'ontario-gta-assets';
export const ONTARIO_GTA_ASSETS_LAYER_TITLE =
  'Government of Ontario GTA assets';

const ONTARIO_GTA_THREAT_SENT_AT = '2025-01-13T16:00:00.000Z';

export const ONTARIO_GTA_EVENT_STARTED_AT = '2025-01-13T12:00:00.000Z';

export const ONTARIO_GTA_THREAT_PROVENANCE =
  '7 Assets to be Impacted by Winter Storm per ECCC Winter Storm Warnings';

export const ONTARIO_GTA_THRESHOLD_DESCRIPTION =
  'Winter storm warning threshold exceeded — Environment Canada snowfall and wind-chill advisory contains Government of Ontario resources in the Greater Toronto Area';

export const ONTARIO_GTA_AUTOMATION_RULE_NAME =
  'Government of Ontario — winter storm warning';
export const ONTARIO_GTA_ICE_RULE_NAME = 'Government of Ontario — ice';
export const ONTARIO_GTA_INFRASTRUCTURE_RULE_NAME =
  'Government of Ontario — infrastructure';

export const ONTARIO_THREAT_TO_RULE_NAME: Record<string, string> = {
  'Winter Storm': ONTARIO_GTA_AUTOMATION_RULE_NAME,
  Ice: ONTARIO_GTA_ICE_RULE_NAME,
  Infrastructure: ONTARIO_GTA_INFRASTRUCTURE_RULE_NAME,
};

export const ONTARIO_GTA_EVENT_SUMMARY =
  'A winter storm is approaching Toronto. Environment Canada has issued a winter storm warning for the Greater Toronto Area with heavy snowfall, blowing snow and dangerous wind chill. Government of Ontario facilities across the GTA will be impacted.';

export const ONTARIO_GTA_LINKED_EVENT_NAME =
  'Winter Storm — Government of Ontario 7 assets in snowstorm path';

export const ONTARIO_GTA_RESOURCES_COMMITTED =
  '7 resources are within the winter storm warning area';

export const ONTARIO_GTA_ORG_EVENT_DEMO_KEY = 'ontario-gta-snowstorm';

export const ONTARIO_GTA_RESOURCE_NOTES_PREFIX = 'ontario-gta:';

export const ONTARIO_GTA_SYMBOLOGY = 'facility';

export const ONTARIO_ASSET_COA_NOTIFY_STORAGE_KEY =
  'ontario-gta-asset-coa-notify-v1';

export const ONTARIO_RESPONSE_CHECKLIST_ITEMS: OrgEventChecklistItemDef[] = [
  {
    id: 'ontario-checklist-accountability',
    kind: 'send_notification',
    label: 'Send accountability check notification',
    notify: {
      notificationTypes: [NotificationType.IN_APP, NotificationType.EMAIL],
      priority: 'warning',
      titleTemplate:
        'Accountability check: Government of Ontario GTA facilities in {eventName} path',
      bodyTemplate:
        'Confirm personnel accountability for Government of Ontario facilities inside the {eventName} warning area. Report status in PRATUS.',
      emailSubject:
        'Accountability check: Government of Ontario GTA facilities — {eventName}',
      emailMessage:
        'Confirm personnel accountability for Government of Ontario facilities inside the {eventName} warning area. Review Courses of Action and assets at risk in PRATUS.',
      appMessage:
        'Accountability check: {eventName} — confirm personnel status for Government of Ontario GTA facilities.',
    },
  },
  {
    id: 'ontario-checklist-salt-plow',
    kind: 'org_action',
    label: 'Confirm salt and plow readiness on priority GTA routes',
    orgAction: { status: 'Planned', poc: null },
  },
  {
    id: 'ontario-checklist-warming-centres',
    kind: 'org_action',
    label: 'Activate warming centres across the GTA',
    orgAction: { status: 'Planned', poc: null },
  },
  {
    id: 'ontario-checklist-transit-highway',
    kind: 'org_action',
    label: 'Set transit and highway closure posture',
    orgAction: { status: 'Planned', poc: null },
  },
];

export const ONTARIO_RESPONSE_CHECKLIST_ORG_ACTION_LABELS =
  ONTARIO_RESPONSE_CHECKLIST_ITEMS.filter(
    (item) => item.kind === 'org_action'
  ).map((item) => item.label);

export function isOntarioGtaOrgActionName(
  name: string | null | undefined
): boolean {
  return !!name && ONTARIO_RESPONSE_CHECKLIST_ORG_ACTION_LABELS.includes(name);
}

export const ONTARIO_ICE_CHECKLIST_ITEMS: OrgEventChecklistItemDef[] = [
  {
    id: 'ontario-ice-treat-walkways',
    kind: 'org_action',
    label: 'Treat facility walkways and loading docks for ice',
    orgAction: { status: 'Planned', poc: null },
  },
  {
    id: 'ontario-ice-accountability',
    kind: 'send_notification',
    label: 'Send ice-advisory accountability check',
    notify: {
      notificationTypes: [NotificationType.IN_APP, NotificationType.EMAIL],
      priority: 'warning',
      titleTemplate: 'Ice accountability check — {eventName}',
      bodyTemplate:
        'Confirm personnel accountability and safe site access as ice risk rises. Report status in PRATUS.',
      emailSubject: 'Ice accountability check — {eventName}',
      emailMessage:
        'Confirm personnel accountability and treat walkways and docks. Review Courses of Action in PRATUS.',
      appMessage:
        'Ice accountability check: confirm personnel status and site access.',
    },
  },
];

export const ONTARIO_INFRASTRUCTURE_CHECKLIST_ITEMS: OrgEventChecklistItemDef[] =
  [
    {
      id: 'ontario-infra-backup-power',
      kind: 'org_action',
      label: 'Verify backup power and comms failover',
      orgAction: { status: 'Planned', poc: null },
    },
    {
      id: 'ontario-infra-ops-notify',
      kind: 'send_notification',
      label: 'Notify ops of infrastructure disruption risk',
      notify: {
        notificationTypes: [NotificationType.IN_APP, NotificationType.EMAIL],
        priority: 'watch',
        titleTemplate: 'Infrastructure watch — {eventName}',
        bodyTemplate:
          'Infrastructure disruption may affect Government of Ontario facility operations. Confirm backup power and site status in PRATUS.',
        emailSubject: 'Infrastructure watch — {eventName}',
        emailMessage:
          'Review backup power, comms failover, and mission essential functions in PRATUS.',
        appMessage:
          'Infrastructure watch: confirm backup power and site status.',
      },
    },
  ];

const WINTER_STORM_THREAT = 'Winter Storm';
const ICE_THREAT = 'Ice';
const INFRASTRUCTURE_THREAT = 'Infrastructure';

export function ontarioChecklistsForThreats(
  threats: string[]
): Record<string, OrgEventChecklistItemDef[]> {
  const checklists: Record<string, OrgEventChecklistItemDef[]> = {};
  for (const threat of threats) {
    if (threat === WINTER_STORM_THREAT) {
      checklists[threat] = ONTARIO_RESPONSE_CHECKLIST_ITEMS;
    } else if (threat === ICE_THREAT) {
      checklists[threat] = ONTARIO_ICE_CHECKLIST_ITEMS;
    } else if (threat === INFRASTRUCTURE_THREAT) {
      checklists[threat] = ONTARIO_INFRASTRUCTURE_CHECKLIST_ITEMS;
    }
  }
  return checklists;
}

export function ontarioChecklistsForAutomationRules(threats: string[]): {
  automationRules: string[];
  automationRuleChecklists: Record<string, OrgEventChecklistItemDef[]>;
} {
  const threatChecklists = ontarioChecklistsForThreats(threats);
  const automationRules: string[] = [];
  const automationRuleChecklists: Record<string, OrgEventChecklistItemDef[]> =
    {};
  for (const threat of threats) {
    const ruleName = ONTARIO_THREAT_TO_RULE_NAME[threat];
    if (!ruleName) continue;
    if (!automationRules.includes(ruleName)) automationRules.push(ruleName);
    const items = threatChecklists[threat];
    if (items) automationRuleChecklists[ruleName] = items;
  }
  return { automationRules, automationRuleChecklists };
}

export type OntarioGtaFacility = {
  id: string;
  name: string;
  city: string;
  state: string;
  lon: number;
  lat: number;
};

export const ONTARIO_GTA_FACILITIES: OntarioGtaFacility[] = [
  {
    id: 'ontario-queens-park',
    name: "Queen's Park Legislative Precinct",
    city: 'Toronto',
    state: 'ON',
    lon: -79.3925,
    lat: 43.6629,
  },
  {
    id: 'ontario-opp-gta',
    name: 'Ontario Provincial Police GTA Detachment',
    city: 'Aurora',
    state: 'ON',
    lon: -79.4504,
    lat: 44.0061,
  },
  {
    id: 'ontario-mto-downsview',
    name: 'Ministry of Transportation Downsview',
    city: 'Toronto',
    state: 'ON',
    lon: -79.4797,
    lat: 43.7499,
  },
  {
    id: 'ontario-paramedic-hq',
    name: 'Toronto Paramedic Services HQ',
    city: 'Toronto',
    state: 'ON',
    lon: -79.3315,
    lat: 43.6588,
  },
  {
    id: 'ontario-opg-pickering',
    name: 'Ontario Power Generation Pickering',
    city: 'Pickering',
    state: 'ON',
    lon: -79.07,
    lat: 43.8117,
  },
  {
    id: 'ontario-hydro-one-etobicoke',
    name: 'Hydro One Etobicoke Transmission',
    city: 'Etobicoke',
    state: 'ON',
    lon: -79.544,
    lat: 43.6205,
  },
  {
    id: 'ontario-peel-eoc',
    name: 'Peel Region Emergency Operations',
    city: 'Brampton',
    state: 'ON',
    lon: -79.7215,
    lat: 43.7256,
  },
];

export type OntarioAssetsTabSeed = OntarioGtaFacility & {
  threats: string[];
  inStormArea?: boolean;
};

const STORM_AREA_EXTRA_THREATS: Record<string, string[]> = {
  'ontario-queens-park': [INFRASTRUCTURE_THREAT],
  'ontario-opp-gta': [ICE_THREAT],
  'ontario-mto-downsview': [ICE_THREAT],
  'ontario-paramedic-hq': [ICE_THREAT],
  'ontario-opg-pickering': [INFRASTRUCTURE_THREAT],
  'ontario-hydro-one-etobicoke': [INFRASTRUCTURE_THREAT],
  'ontario-peel-eoc': [ICE_THREAT],
};

export const ONTARIO_ASSETS_TAB_SEEDS: OntarioAssetsTabSeed[] =
  ONTARIO_GTA_FACILITIES.map((facility) => ({
    ...facility,
    inStormArea: true,
    threats: [
      WINTER_STORM_THREAT,
      ...(STORM_AREA_EXTRA_THREATS[facility.id] ?? []),
    ],
  }));

export function formatOntarioFacilityLabel(
  facility: OntarioGtaFacility
): string {
  return `${facility.name} (${facility.city}, ${facility.state})`;
}

export function ontarioGtaResourceNotesMarker(facilityId: string): string {
  return `${ONTARIO_GTA_RESOURCE_NOTES_PREFIX}${facilityId}`;
}

export function ontarioFacilityIdFromResourceNotes(
  notes: string | null | undefined
): string | undefined {
  if (!notes?.startsWith(ONTARIO_GTA_RESOURCE_NOTES_PREFIX)) return undefined;
  const id = notes.slice(ONTARIO_GTA_RESOURCE_NOTES_PREFIX.length);
  return id || undefined;
}

export function isOntarioGtaResourceSet(resource: {
  notes?: string | null;
}): boolean {
  return !!resource.notes?.startsWith(ONTARIO_GTA_RESOURCE_NOTES_PREFIX);
}

function buildAssetFromFacility(
  facility: OntarioGtaFacility
): MemphisAssetAtRisk {
  return {
    id: facility.id,
    resource: formatOntarioFacilityLabel(facility),
    risk:
      `${formatOntarioFacilityLabel(facility)} sits inside the ` +
      `Environment Canada winter storm warning for the Greater Toronto Area. ` +
      `Confirm salt and plow readiness, warming-centre posture and report status.`,
    location: [facility.lon, facility.lat],
    operationalStatus: 'Partially Operational',
  };
}

export function getOntarioRegionalThreats(): MemphisRegionalThreats {
  return {
    region: 'Greater Toronto Area — winter storm warning',
    description:
      'A winter storm approaching Toronto places the following Government of Ontario resources inside the Environment Canada warning area at elevated risk:',
    threats: ONTARIO_GTA_FACILITIES.map(buildAssetFromFacility),
    responseChecklist: ONTARIO_RESPONSE_CHECKLIST_ITEMS.map((item) => ({
      id: item.id,
      label: item.label,
    })),
  };
}

export function isHardcodedOntarioGtaThreatNotification(
  notificationId: string | null | undefined
): boolean {
  return notificationId === ONTARIO_GTA_THREAT_NOTIFICATION_ID;
}

export function getOntarioGtaThreatNotification(
  groupId: string
): UserNotificationWithDetails {
  return {
    userGuid: 'system',
    notificationId: ONTARIO_GTA_THREAT_NOTIFICATION_ID,
    groupId,
    archived: false,
    acknowledged: false,
    canArchive: false,
    createdAt: ONTARIO_GTA_THREAT_SENT_AT,
    acknowledgedAt: null,
    archivedAt: null,
    priority: NotificationPriority.WARNING,
    status: 'sent',
    acknowledgementRequired: false,
    sourceItemId: '',
    sourceItemType: ITEM_TYPES.Sitrep,
    sentAt: ONTARIO_GTA_THREAT_SENT_AT,
    scheduledDate: null,
    incidentId: null,
    aors: [],
    appMessage:
      'Likely impact to Government of Ontario resources due to Winter Storm in the Greater Toronto Area.',
    emailSubject: null,
    emailMessage: null,
    smsMessage: null,
    teamsMessage: null,
    sendEmail: null,
    sendSms: null,
    sendTeams: null,
    actionType: null,
    createdByName: ONTARIO_GTA_THREAT_PROVENANCE,
  };
}
